import {IssueTypeToken} from '@github-ui/issue-type-token'
import {useAnalytics} from '@github-ui/use-analytics'
import {InfoIcon, IssueOpenedIcon} from '@primer/octicons-react'
import {AnchoredOverlay, IconButton} from '@primer/react'
import {useCallback, useState} from 'react'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {LABELS} from '../constants/labels'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {IssueTypeAddedEventRationales$data} from './__generated__/IssueTypeAddedEventRationales.graphql'
import type {IssueTypeChangedEvent$key} from './__generated__/IssueTypeChangedEvent.graphql'
import styles from './IssueTypeEvent.module.css'
import {TimelineRow} from './row/TimelineRow'

type IssueTypeChangedEventProps = {
  queryRef: IssueTypeChangedEvent$key & {createdAt?: string}
  issueUrl: string
  onLinkClick?: (event: MouseEvent) => void
  highlightedEventId?: string
  refAttribute?: React.RefObject<HTMLDivElement | null>
  repositoryNameWithOwner?: string
  rationale?: IssueTypeAddedEventRationales$data['eventRationales'][number]
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const IssueTypeChangedEventFragment = graphql`
  fragment IssueTypeChangedEvent on IssueTypeChangedEvent {
    databaseId
    actor {
      ...TimelineRowEventActor
    }
    createdAt
    issueType {
      name
      color
    }
    prevIssueType {
      name
      color
    }
  }
`

export function IssueTypeChangedEvent({
  queryRef,
  issueUrl,
  onLinkClick,
  highlightedEventId,
  refAttribute,
  repositoryNameWithOwner,
  rationale,
}: IssueTypeChangedEventProps) {
  const {actor, createdAt, issueType, prevIssueType, databaseId} = useFragment(IssueTypeChangedEventFragment, queryRef)
  const {sendAnalyticsEvent} = useAnalytics()
  const [showRationale, setShowRationale] = useState(false)
  const toggleRationale = useCallback(() => {
    setShowRationale(prev => {
      const next = !prev
      if (next) {
        sendAnalyticsEvent('issue_viewer.timeline.rationale_click', 'ISSUE_TYPE_CHANGED', {
          issueEventDatabaseId: String(databaseId),
          issueTypeName: issueType?.name,
          rationaleLength: rationale?.rationale?.length,
        })
      }
      return next
    })
  }, [sendAnalyticsEvent, databaseId, issueType?.name, rationale?.rationale])
  const closeRationale = useCallback(() => setShowRationale(false), [])

  if (!issueType) {
    return null
  }

  const highlighted = String(databaseId) === highlightedEventId

  const getTooltipText = (isTextTruncated: boolean) => {
    return isTextTruncated ? issueType.name : undefined
  }

  if (!prevIssueType) {
    return null
  }

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      onLinkClick={onLinkClick}
      leadingIcon={IssueOpenedIcon}
    >
      <TimelineRow.Main>
        {`${LABELS.timeline.issueTypeChanged.leading}`}
        <div className={styles.issueTypeTokenWrapper}>
          <IssueTypeToken
            name={prevIssueType.name}
            color={prevIssueType.color}
            href={`/${repositoryNameWithOwner}/issues?q=type:"${prevIssueType.name}"`}
            getTooltipText={getTooltipText}
            size="small"
          />
        </div>
        {`${LABELS.timeline.issueTypeChanged.trailing}`}
        <div className={styles.issueTypeTokenWrapper}>
          <IssueTypeToken
            name={issueType.name}
            color={issueType.color}
            href={`/${repositoryNameWithOwner}/issues?q=type:"${issueType.name}"`}
            getTooltipText={getTooltipText}
            size="small"
          />
        </div>
        {rationale && (
          <AnchoredOverlay
            open={showRationale}
            onOpen={toggleRationale}
            onClose={closeRationale}
            renderAnchor={props => (
              // eslint-disable-next-line primer-react/a11y-remove-disable-tooltip
              <IconButton
                {...props}
                icon={InfoIcon}
                variant="invisible"
                size="small"
                aria-label="Rationale"
                unsafeDisableTooltip
                className={styles.rationaleIcon}
              />
            )}
          >
            <div className={styles.rationaleOverlay}>{rationale.rationale}</div>
          </AnchoredOverlay>
        )}
      </TimelineRow.Main>
    </TimelineRow>
  )
}
